import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const demos = [
  { role: "Patient", email: "patient@doccare.demo", password: "Patient@123" },
  { role: "Doctor", email: "dr.chen@doccare.demo", password: "Doctor@123" },
  { role: "Admin", email: "admin@doccare.demo", password: "Admin@123" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (e, override) => {
    e?.preventDefault?.();
    const em = override?.email || email;
    const pw = override?.password || password;
    setLoading(true);
    try {
      const user = await login(em, pw);
      toast.success(`Welcome, ${user.name || user.email}`);
      const from = loc.state?.from;
      const home = user.role === "doctor" ? "/doctor/dashboard" : user.role === "admin" ? "/admin/dashboard" : "/patient/dashboard";
      nav(from || home, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block bg-slate-900 text-white p-12 relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-white text-slate-900 grid place-items-center font-bold">D</div>
          <span className="text-xl font-bold">DocCare</span>
        </Link>
        <div className="mt-24 max-w-md">
          <h1 className="text-4xl font-bold leading-tight">Healthcare appointments, made simple.</h1>
          <p className="mt-4 text-slate-300">Sign in to manage your bookings, view availability and stay on top of your care.</p>
        </div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-emerald-500/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-bold">D</div>
            <span className="text-xl font-bold text-slate-900">DocCare</span>
          </Link>

          <h2 className="text-3xl font-bold text-slate-900">Sign in</h2>
          <p className="text-slate-500 mt-2">Welcome back — enter your details to continue.</p>

          <form onSubmit={doLogin} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl h-11" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl h-11" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-xl h-11" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            No account?{" "}
            <Link to="/register" className="font-semibold text-slate-900 underline" data-testid="link-register">Create one</Link>
          </p>

          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Demo accounts</p>
            <div className="mt-3 space-y-2">
              {demos.map((d) => (
                <button
                  key={d.role}
                  onClick={(e) => doLogin(e, { email: d.email, password: d.password })}
                  className="w-full text-left rounded-xl bg-slate-50 hover:bg-slate-100 px-3 py-2 flex items-center justify-between text-sm transition-colors"
                  data-testid={`demo-${d.role.toLowerCase()}`}
                >
                  <span><span className="font-semibold">{d.role}</span> · {d.email}</span>
                  <span className="text-xs text-slate-500">Tap to sign in</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
